import {
    createContext,
    useCallback,
    useContext,
    useState,
    useEffect,
    type PropsWithChildren,
    ComponentProps,
} from 'react';
import Drawer, { DrawerContent } from 'shared/Drawer';

type ShowDrawerProps = Omit<
    ComponentProps<typeof Drawer>,
    'visible' | 'onClose'
> & {
    closeCb?: () => void;
};

export interface DrawerContextType {
    showDrawer: (props: ShowDrawerProps) => void;
    closeDrawer: () => void;
}

export const DrawerContext = createContext<DrawerContextType>(null!);

export const DrawerProvider = ({ children }: PropsWithChildren) => {
    const [visible, setVisible] = useState(false);
    const [showDrawerProps, setShowDrawerProps] =
        useState<ShowDrawerProps | null>(null);

    const showDrawer = useCallback((props: ShowDrawerProps) => {
        setVisible(true);
        setShowDrawerProps(props);
    }, []);

    const closeDrawer = useCallback(() => {
        if (!visible) {
            return;
        }

        document.body.classList.toggle('overflow-hidden', false);
        setVisible(false);
        showDrawerProps?.closeCb?.();
    }, [showDrawerProps, visible]);

    useEffect(() => {
        if (visible) {
            document.body.classList.toggle('overflow-hidden', true);
        }

        return () => {
            document.body.classList.toggle('overflow-hidden', false);
        };
    }, [visible]);

    return (
        <DrawerContext value={{ showDrawer, closeDrawer }}>
            <>
                <Drawer
                    title={showDrawerProps?.title ?? ''}
                    titleBadge={showDrawerProps?.titleBadge}
                    visible={visible}
                    onClose={closeDrawer}
                    header={showDrawerProps?.header}
                    footerButtons={showDrawerProps?.footerButtons}
                >
                    <DrawerContent>{showDrawerProps?.children}</DrawerContent>
                </Drawer>

                {children}
            </>
        </DrawerContext>
    );
};

export const useDrawerContext = () => {
    const drawerContext = useContext(DrawerContext);

    if (!drawerContext) {
        throw new Error(
            'useDrawerContext must be used within a DrawerProvider'
        );
    }

    return drawerContext;
};
